/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import { SeverityLevel } from "@microsoft/applicationinsights-web";
import UIConstants from "../../UIConstants";
import appInsights from "../AppInsights/appInsightsTelemetryClient";

export type HttpMethod = "GET" | "POST" | "DELETE" | "PUT";

export type Headers = RequestInit["headers"];

export interface HttpClientOptions {
    disableAppInsights?: boolean;
}

export default class HttpClient {
    public constructor(private readonly _options?: HttpClientOptions) {}

    public get(path: string): Promise<Response> {
        return this.request(path);
    }

    public post<TBody>(path: string, body?: TBody, headers?: Headers): Promise<Response> {
        return this.request(path, "POST", body, headers);
    }

    public put<TBody>(path: string, body?: TBody, headers?: Headers): Promise<Response> {
        return this.request(path, "PUT", body, headers);
    }

    public delete(path: string): Promise<Response> {
        return this.request(path, "DELETE")
    }

    private async request<TBody>(path: string, method: HttpMethod = "GET", body?: TBody, headers?: Headers): Promise<Response> {
        try {
            const resp = await fetch(path, this.getFetchOptions(method, body, headers));
            return resp;
        } catch (err) {
            this.trackError(path, err);
            throw err;
        }
    }

    private trackError(path: string, err: unknown) {
        !this._options?.disableAppInsights &&
            appInsights.trackTrace(
                {
                    message: `Fehler bei der Anfrage von ${path}`,
                    severityLevel: SeverityLevel.Error,
                },
                { error: err });
    }

    private getFetchOptions<TBody>(method: HttpMethod, body?: TBody, headers?: Headers): RequestInit {
        const fetchOptions: RequestInit = { credentials: UIConstants.credentials };
        fetchOptions.method = method;
        if (method === "POST" || method === "PUT") {
            fetchOptions.headers = { "Content-Type": "application/json; charset=utf-8" };
            fetchOptions.body = JSON.stringify(body);
        }

        fetchOptions.headers = { ...(fetchOptions.headers ?? {}), ...(headers ?? {}) };

        return fetchOptions;
    }
}